"use client";

import { useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CrosswordEntry,
  type CrosswordLayout,
  layoutCrossword,
  MAX_GRID_SIZE,
  prepareCrosswordWords,
  type RejectReason,
} from "@/lib/tool-kit/classroom/crossword";
import { parseTermLines } from "@/lib/tool-kit/classroom/entries";
import { secureRandom } from "@/lib/tool-kit/classroom/random";

/** Headings inside the printable sheet inherit its color, so dark mode prints black. */
const SHEET_H2 = "font-semibold text-xl";
const SHEET_H3 = "font-semibold text-base";
const MIN_WORDS = 2;
const MAX_WORDS = 40;
const CELL = 10;

/** English strings. A translated content file overrides these through `ui`. */
export const CROSSWORD_PUZZLE_GENERATOR_STRINGS = {
  wordsLabel: "Answers and clues — one pair per line, answer first",
  wordsHint:
    "Separate the answer from its clue with a dash, colon or tab. Spaces and hyphens in an answer are left out of the grid.",
  wordsPlaceholder:
    "photosynthesis - How plants make glucose from light\nchlorophyll - Green pigment that absorbs light",
  example:
    "photosynthesis - How plants make glucose from light\nchlorophyll - Green pigment that absorbs light\nstomata - Pores in a leaf that let gases in and out\nglucose - Sugar made during photosynthesis\noxygen - Gas released as a by-product\ncarbon dioxide - Gas a leaf takes in from the air\nxylem - Tissue that carries water up the stem\nphloem - Tissue that carries sugars around the plant\nroots - Anchor the plant and absorb water\nsunlight - Energy source for photosynthesis",
  loadExample: "Load example",
  answerCount: "one:{count} answer|other:{count} answers",
  titleLabel: "Puzzle title",
  titlePlaceholder: "Plant Biology Crossword",
  defaultTitle: "Crossword",
  options: "Options",
  showLengths: "Show answer lengths after clues, like (6) or (3,5)",
  showBank: "Add a word bank (easier puzzle)",
  showNameLine: "Name and date line",
  generate: "Generate crossword",
  regenerate: "Regenerate layout",
  printPuzzle: "Print puzzle",
  printKey: "Print answer key",
  showKey: "Show answer key",
  hideKey: "Hide answer key",
  across: "Across",
  down: "Down",
  wordBank: "Word bank",
  answerKeyTitle: "{title} — Answer key",
  name: "Name",
  date: "Date",
  gridLabel: "Crossword grid, {cols} columns by {rows} rows",
  keyGridLabel: "Completed crossword grid",
  crossings: "one:{count} crossing|other:{count} crossings",
  summary:
    "Placed {placed} of {total} answers on a {cols} × {rows} grid, with {crossings}.",
  unplaced:
    "Not placed: {words}. No legal crossing was left for these. Try Regenerate layout, or add an answer that shares letters with them.",
  invalidLines:
    "Lines without a clue were skipped: {lines}. Write each line as answer - clue.",
  rejectedLetters: "Letters only, so these were skipped: {words}",
  rejectedShort: "Answers need at least 2 letters: {words}",
  rejectedLong: "Answers can have at most {max} letters: {words}",
  rejectedDuplicate: "Repeated answers were used once: {words}",
  needWords: "Enter at least {min} answers with clues.",
  tooMany:
    "Use at most {max} answers — a bigger grid is hard to print and solve.",
} as const;

type Strings = typeof CROSSWORD_PUZZLE_GENERATOR_STRINGS;
type PrintTarget = "puzzle" | "key";

const REJECT_MESSAGES: Record<RejectReason, keyof Strings> = {
  letters: "rejectedLetters",
  short: "rejectedShort",
  long: "rejectedLong",
  duplicate: "rejectedDuplicate",
};

function rejectionNotes(
  rejected: { term: string; reason: RejectReason }[],
  strings: Strings
): string[] {
  return (Object.keys(REJECT_MESSAGES) as RejectReason[]).flatMap((reason) => {
    const words = rejected
      .filter((r) => r.reason === reason)
      .map((r) => r.term);
    return words.length === 0
      ? []
      : [
          t(strings[REJECT_MESSAGES[reason]], {
            words: words.join(", "),
            max: MAX_GRID_SIZE,
          }),
        ];
  });
}

function Grid({
  layout,
  filled,
  label,
}: {
  layout: CrosswordLayout;
  filled: boolean;
  label: string;
}) {
  const cells = layout.grid.flatMap((row, r) =>
    row.flatMap((letter, c) =>
      letter === null
        ? []
        : [{ id: `${r}:${c}`, r, c, letter, number: layout.numbers[r]?.[c] }]
    )
  );
  return (
    <svg
      aria-label={label}
      className="mx-auto block h-auto w-full"
      role="img"
      style={{ maxWidth: `${layout.cols * 2.2}rem` }}
      viewBox={`-0.5 -0.5 ${layout.cols * CELL + 1} ${layout.rows * CELL + 1}`}
    >
      {cells.map((cell) => (
        <g key={cell.id}>
          <rect
            fill="#ffffff"
            height={CELL}
            stroke="#111111"
            strokeWidth={0.5}
            width={CELL}
            x={cell.c * CELL}
            y={cell.r * CELL}
          />
          {cell.number ? (
            <text
              fill="#111111"
              fontSize={3}
              x={cell.c * CELL + 0.8}
              y={cell.r * CELL + 3.1}
            >
              {cell.number}
            </text>
          ) : null}
          {filled ? (
            <text
              fill="#111111"
              fontSize={6}
              fontWeight={600}
              textAnchor="middle"
              x={cell.c * CELL + CELL / 2}
              y={cell.r * CELL + 7.6}
            >
              {cell.letter}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

function ClueList({
  heading,
  entries,
  showLengths,
  answers,
}: {
  heading: string;
  entries: CrosswordEntry[];
  showLengths: boolean;
  answers: boolean;
}) {
  return (
    <div className="space-y-2">
      <h3 className={SHEET_H3}>{heading}</h3>
      <ol className="space-y-1 text-sm leading-relaxed">
        {entries.map((entry) => (
          <li className="flex gap-2" key={`${entry.direction}-${entry.number}`}>
            <span className="w-6 shrink-0 text-right font-semibold">
              {entry.number}
            </span>
            <span>
              {answers ? entry.letters.join("") : entry.clue}
              {showLengths && !answers ? ` ${entry.enumeration}` : ""}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function NameLine({ strings }: { strings: Strings }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
      <span className="flex items-end gap-2">
        {strings.name}
        <span className="inline-block w-56 border-current border-b" />
      </span>
      <span className="flex items-end gap-2">
        {strings.date}
        <span className="inline-block w-32 border-current border-b" />
      </span>
    </div>
  );
}

function Status({
  layout,
  notes,
  error,
  strings,
  locale,
}: {
  layout: CrosswordLayout | null;
  notes: string[];
  error: string;
  strings: Strings;
  locale: string;
}) {
  const placed = layout ? layout.across.length + layout.down.length : 0;
  return (
    <div aria-live="polite" className="space-y-3 print:hidden">
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
      {layout && layout.unplaced.length > 0 ? (
        <Notice>
          {t(strings.unplaced, {
            words: layout.unplaced.map((word) => word.term).join(", "),
          })}
        </Notice>
      ) : null}
      {layout ? (
        <p className={HINT}>
          {t(strings.summary, {
            placed,
            total: placed + layout.unplaced.length,
            cols: layout.cols,
            rows: layout.rows,
            crossings: tPlural(
              strings.crossings,
              locale,
              layout.intersections,
              {
                count: layout.intersections,
              }
            ),
          })}
        </p>
      ) : null}
    </div>
  );
}

interface SheetProps {
  layout: CrosswordLayout;
  heading: string;
  strings: Strings;
  locale: string;
  showLengths: boolean;
  showBank: boolean;
  showNameLine: boolean;
  className: string;
}

function PuzzleSheet({
  layout,
  heading,
  strings,
  locale,
  showLengths,
  showBank,
  showNameLine,
  className,
}: SheetProps) {
  const bank = [...layout.across, ...layout.down]
    .map((entry) => entry.term.toLocaleUpperCase(locale))
    .sort((a, b) => a.localeCompare(b, locale));
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>{heading}</h2>
      {showNameLine ? <NameLine strings={strings} /> : null}
      <Grid
        filled={false}
        label={t(strings.gridLabel, { cols: layout.cols, rows: layout.rows })}
        layout={layout}
      />
      <div className="grid gap-6 sm:grid-cols-2 print:grid-cols-2">
        <ClueList
          answers={false}
          entries={layout.across}
          heading={strings.across}
          showLengths={showLengths}
        />
        <ClueList
          answers={false}
          entries={layout.down}
          heading={strings.down}
          showLengths={showLengths}
        />
      </div>
      {showBank ? (
        <div className="space-y-2">
          <h3 className={SHEET_H3}>{strings.wordBank}</h3>
          <p className="text-sm leading-relaxed">{bank.join(" · ")}</p>
        </div>
      ) : null}
    </section>
  );
}

function KeySheet({
  layout,
  heading,
  strings,
  className,
}: Pick<SheetProps, "layout" | "heading" | "strings" | "className">) {
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>
        {t(strings.answerKeyTitle, { title: heading })}
      </h2>
      <Grid filled label={strings.keyGridLabel} layout={layout} />
      <div className="grid gap-6 sm:grid-cols-2 print:grid-cols-2">
        <ClueList
          answers
          entries={layout.across}
          heading={strings.across}
          showLengths={false}
        />
        <ClueList
          answers
          entries={layout.down}
          heading={strings.down}
          showLengths={false}
        />
      </div>
    </section>
  );
}

export default function CrosswordPuzzleGeneratorWidget() {
  const strings = useToolUi(CROSSWORD_PUZZLE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [title, setTitle] = useState("");
  const [showLengths, setShowLengths] = useState(true);
  const [showBank, setShowBank] = useState(false);
  const [showNameLine, setShowNameLine] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("puzzle");
  const [layout, setLayout] = useState<CrosswordLayout | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [error, setError] = useState("");

  const parsed = parseTermLines(input);
  const heading = title.trim() || strings.defaultTitle;

  function handleGenerate() {
    const { words, rejected } = prepareCrosswordWords(parsed.entries, locale);
    const nextNotes = rejectionNotes(rejected, strings);
    if (parsed.invalidLines.length > 0) {
      nextNotes.unshift(
        t(strings.invalidLines, { lines: parsed.invalidLines.join(", ") })
      );
    }
    setNotes(nextNotes);
    if (words.length < MIN_WORDS) {
      setError(t(strings.needWords, { min: MIN_WORDS }));
      setLayout(null);
      return;
    }
    if (words.length > MAX_WORDS) {
      setError(t(strings.tooMany, { max: MAX_WORDS }));
      setLayout(null);
      return;
    }
    setError("");
    setLayout(layoutCrossword(words, { random: secureRandom }));
  }

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  const sheet = "space-y-6 text-foreground print:text-black";
  const keyVisibility = showKey ? "" : "hidden";
  const keyPrint = printTarget === "key" ? "print:block" : "print:hidden";

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <TextAreaField
          hint={
            <>
              {strings.wordsHint}{" "}
              {tPlural(strings.answerCount, locale, parsed.entries.length, {
                count: parsed.entries.length,
              })}
            </>
          }
          id="cw-words"
          label={strings.wordsLabel}
          onChange={setInput}
          placeholder={strings.wordsPlaceholder}
          prose
          value={input}
        />
        <button
          className={BUTTON_SMALL}
          onClick={() => setInput(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <TextField
          id="cw-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <fieldset className="space-y-2">
          <legend className="font-medium text-foreground text-sm">
            {strings.options}
          </legend>
          <Checkbox
            checked={showLengths}
            label={strings.showLengths}
            onChange={setShowLengths}
          />
          <Checkbox
            checked={showBank}
            label={strings.showBank}
            onChange={setShowBank}
          />
          <Checkbox
            checked={showNameLine}
            label={strings.showNameLine}
            onChange={setShowNameLine}
          />
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={parsed.entries.length === 0}
            onClick={handleGenerate}
            type="button"
          >
            {layout ? strings.regenerate : strings.generate}
          </button>
          {layout ? (
            <>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("puzzle")}
                type="button"
              >
                {strings.printPuzzle}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => print("key")}
                type="button"
              >
                {strings.printKey}
              </button>
              <button
                aria-pressed={showKey}
                className={BUTTON_SECONDARY}
                onClick={() => setShowKey((value) => !value)}
                type="button"
              >
                {showKey ? strings.hideKey : strings.showKey}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <Status
        error={error}
        layout={layout}
        locale={locale}
        notes={notes}
        strings={strings}
      />

      {layout ? (
        <PuzzleSheet
          className={`${sheet} ${printTarget === "key" ? "print:hidden" : ""}`}
          heading={heading}
          layout={layout}
          locale={locale}
          showBank={showBank}
          showLengths={showLengths}
          showNameLine={showNameLine}
          strings={strings}
        />
      ) : null}
      {layout ? (
        <KeySheet
          className={`${sheet} ${keyVisibility} ${keyPrint}`}
          heading={heading}
          layout={layout}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
